import { Box, SimpleGrid, Group, Button, Text } from '@mantine/core'
import { IoTrashOutline, IoStarOutline } from 'react-icons/io5'
import type { ContentPhotoDto } from '../../app/models/contentPhoto'

type Props = {
  photos: ContentPhotoDto[];
  canManage: boolean;
  onDelete: (id: string) => void | Promise<void>;
  onSetMain: (id: string) => void | Promise<void>;
  /** build: compact thumbs; post: 4:3 + caption; part: thumbs with per-photo delete rules */
  variant?: 'build' | 'post' | 'part';
  /** When set, controls which photos show Delete (e.g. part: own upload or admin). Default: all cells if canManage. */
  canDeletePhoto?: (p: ContentPhotoDto) => boolean;
  /** When false, hides Set cover even if canManage (default: same as canManage). */
  canSetMain?: boolean;
  /** Called when an image is clicked; receives the full URL. */
  onImageClick?: (url: string) => void;
};

/**
 * Thumbnail grid for builds/parts; post variant uses 4:3 cells and optional captions.
 */
export default function PhotoGallery ({
  photos,
  canManage,
  onDelete,
  onSetMain,
  variant = 'build',
  canDeletePhoto,
  canSetMain: canSetMainProp,
  onImageClick
}: Props) {
  if (photos.length === 0) return null

  const showSetMain = canSetMainProp ?? canManage
  const showDelete = (p: ContentPhotoDto) => {
    if (!canManage) return false
    return canDeletePhoto ? canDeletePhoto(p) : true
  }

  if (variant === 'post') {
    return (
      <SimpleGrid cols={2} breakpoints={[{ minWidth: 520, cols: 3 }]} spacing="md">
        {photos.map((p) => (
          <Box key={p.id}>
            <Box
              onClick={onImageClick ? () => onImageClick(p.url) : undefined}
              sx={{
                position: 'relative',
                width: '100%',
                aspectRatio: '4 / 3',
                borderRadius: 8,
                overflow: 'hidden',
                border: p.isMain ? '2px solid var(--gs-accent)' : '1px solid var(--gs-border)',
                background: 'var(--gs-surface2)',
                cursor: onImageClick ? 'zoom-in' : undefined
              }}
            >
              <Box
                component="img"
                src={p.url}
                alt=""
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block'
                }}
              />
            </Box>
            {p.caption
              ? (
              <Text size="xs" color="dimmed" mt={8} sx={{ lineHeight: 1.45, fontFamily: 'DM Sans, sans-serif' }}>
                {p.caption}
              </Text>
                )
              : null}
            {canManage && (showSetMain || showDelete(p)) && (
              <Group spacing={4} mt={8} sx={{ flexWrap: 'wrap' }}>
                {showSetMain && !p.isMain && (
                  <Button
                    type="button"
                    variant="subtle"
                    size="xs"
                    compact
                    leftIcon={<IoStarOutline size={14} />}
                    onClick={() => void onSetMain(p.id)}
                    sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11 }}
                  >
                    Set cover
                  </Button>
                )}
                {showDelete(p) && (
                  <Button
                    type="button"
                    variant="subtle"
                    size="xs"
                    compact
                    color="red"
                    leftIcon={<IoTrashOutline size={14} />}
                    onClick={() => void onDelete(p.id)}
                    sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11 }}
                  >
                    Delete
                  </Button>
                )}
              </Group>
            )}
          </Box>
        ))}
      </SimpleGrid>
    )
  }

  return (
    <SimpleGrid cols={2} breakpoints={[{ minWidth: 480, cols: 3 }, { minWidth: 720, cols: 4 }]} spacing="sm">
      {photos.map((p) => (
        <Box key={p.id}>
          <Box
            component="img"
            src={p.url}
            alt=""
            onClick={onImageClick ? () => onImageClick(p.url) : undefined}
            sx={{
              width: '100%',
              height: 112,
              objectFit: 'cover',
              borderRadius: 8,
              border: p.isMain ? '2px solid var(--gs-accent)' : '1px solid var(--gs-border)',
              display: 'block',
              background: 'var(--gs-surface2)',
              cursor: onImageClick ? 'zoom-in' : undefined
            }}
          />
          {canManage && (showSetMain || showDelete(p)) && (
            <Group spacing={4} mt={6} sx={{ flexWrap: 'wrap' }}>
              {showSetMain && !p.isMain && (
                <Button
                  type="button"
                  variant="subtle"
                  size="xs"
                  compact
                  leftIcon={<IoStarOutline size={14} />}
                  onClick={() => void onSetMain(p.id)}
                  sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11 }}
                >
                  Set cover
                </Button>
              )}
              {showDelete(p) && (
                <Button
                  type="button"
                  variant="subtle"
                  size="xs"
                  compact
                  color="red"
                  leftIcon={<IoTrashOutline size={14} />}
                  onClick={() => void onDelete(p.id)}
                  sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11 }}
                >
                  Delete
                </Button>
              )}
            </Group>
          )}
        </Box>
      ))}
    </SimpleGrid>
  )
}
